import styles from "./checkout-review.module.css";
import { useCheckout } from "BEDROCK/pages/checkout/checkout-provider";
import { CTAButton } from "BEDROCK/shared-components/cta-button";
import { useProjectStore } from "entity-store/project-store/use-project-store";
import { usePaths } from "BEDROCK/paths";
import { ActivityIndicator } from "BEDROCK/shared-components/activity-indicator";
import { useParams } from "react-router-dom";
import { ProjectPreview } from "components/common/project-preview";
import { useSizePresets } from "PosterEditor/model/size-presets/size-preset";
import { UIIcon } from "PosterEditor/common-components/ui-icon-drawing/ui-icon";
// import { SizeComparison } from "./size-comparison";

interface CheckoutReviewProps {}

export const CheckoutReview = (props: CheckoutReviewProps) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { addProject: addToCheckout } = useCheckout();
  const { projects, isLoading, isFetching } = useProjectStore();
  const { navigateTo, checkout: checkoutPath, processOrder } = usePaths();
  const {
    fullThumbnailSizeFromProject,
    sizePresetFromProject,
    sizeInCMFromProject,
  } = useSizePresets();

  // Get the project by ID from route params
  const project = projects?.find((p) => p.id === projectId);
  const thumbnailSize = project
    ? fullThumbnailSizeFromProject(project)
    : undefined;
  const sizePreset = project ? sizePresetFromProject(project) : undefined;
  const sizeInCM = project ? sizeInCMFromProject(project) : undefined;

  const handleConfirm = async () => {
    if (project) {
      await addToCheckout(project.id);
      navigateTo(checkoutPath);
    }
  };

  const handleKeepEditing = () => {
    if (project) {
      navigateTo(processOrder + "/" + project.id);
    }
  };

  // Show loading state during initial load, refetch, or when projects haven't loaded yet
  const shouldShowLoading = isLoading || isFetching || projects === undefined;

  if (shouldShowLoading && !project) {
    return (
      <div className={styles.container}>
        <div className={styles.reviewContainer}>
          <div className={styles.reviewHeader}>
            <h1>REVIEW YOUR ORDER</h1>
          </div>
          <div className={styles.loadingState}>
            <ActivityIndicator size="medium" />
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className={styles.container}>
        <div className={styles.reviewContainer}>
          <div className={styles.reviewHeader}>
            <h1>REVIEW YOUR ORDER</h1>
          </div>
          <div className={styles.emptyState}>
            <p className={styles.emptyMessage}>
              No project found for review. Please start a new project.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.reviewContainer}>
        <div className={styles.reviewHeader}>
          <h1>REVIEW YOUR ORDER</h1>
        </div>

        <div className={styles.reviewContent}>
          {/* Preview with integrated size caption */}
          <figure className={styles.previewFigure}>
            {/* Resolution warning banner */}
            <div className={styles.resolutionBanner}>
              <UIIcon iconName="INFO" size={24} variant="REGULAR" />
              <p>
                <b>This is a low resolution preview.</b> Your final print will
                be high resolution.
              </p>
            </div>
            <div className={styles.previewImageContainer}>
              {thumbnailSize ? (
                <ProjectPreview
                  project={project}
                  size={thumbnailSize}
                  showOutline={false}
                  showShadow={true}
                  fallbackToMapImage={false}
                />
              ) : (
                <div className={styles.noPreview}>
                  <p>Preview not available</p>
                </div>
              )}
            </div>
            {sizePreset && sizeInCM && (
              <figcaption className={styles.previewCaption}>
                {/* <SizeComparison
                  widthInches={sizeInCM.width / 2.54}
                  heightInches={sizeInCM.height / 2.54}
                /> */}
                <span>
                  {sizePreset.sizeDescription} — {sizeInCM.width} ×{" "}
                  {sizeInCM.height} cm
                </span>
              </figcaption>
            )}
          </figure>

          {/* Quality review info */}
          <div className={styles.infoSection}>
            <p className={styles.infoText}>
              Your order will be reviewed by our team before printing. If we
              notice any issues, we'll email you to confirm before proceeding.
              Please check the preview for any misspellings, positioning, or
              design issues you'd like to correct.
            </p>

            <details className={styles.policyDetails}>
              <summary className={styles.policySummary}>
                Customization Policy & Returns
              </summary>
              <div className={styles.policyContent}>
                <ul className={styles.policyList}>
                  <li>
                    Returns and refunds will not be granted for issues visible
                    in this preview (misspellings, positioning, or design
                    choices).
                  </li>
                  <li>
                    The 14-day cooling-off period does not apply to customizable
                    products.
                  </li>
                  <li>
                    Refunds are only offered if the product is defective or
                    damaged during shipping.
                  </li>
                </ul>
              </div>
            </details>
          </div>

          {/* Actions */}
          <div className={styles.actionButtons}>
            <CTAButton variant="SECONDARY" onClick={handleKeepEditing}>
              Keep Editing
            </CTAButton>
            <CTAButton
              variant="PRIMARY"
              onClick={handleConfirm}
              iconName="CHEVRON-RIGHT"
              iconPosition="RIGHT"
            >
              Confirm & Continue to Checkout
            </CTAButton>
          </div>
        </div>
      </div>
    </div>
  );
};
